import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { X, Sparkles, Copy, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SESSION_KEY = 'ss_exit_popup_shown';

/**
 * Desktop exit-intent popup. Fires once per browser session when the user moves
 * the mouse out of the top of the viewport AND has items in the cart AND a
 * coupon hasn't already been applied. Skips mobile entirely (no exit-intent
 * gesture on touch + would feel intrusive).
 */
export const ExitIntentPopup = ({ cartItems = [] }) => {
  const [open, setOpen] = useState(false);
  const [coupon, setCoupon] = useState(null);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  // Fetch the active coupon up front so the popup is ready when the trigger fires
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/coupons/active`, { timeout: 5000 })
      .then((r) => { if (!cancelled) setCoupon(r.data?.coupon || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Skip mobile / when no cart / coupon / already shown / coupon already applied
    if (typeof window === 'undefined') return undefined;
    if (window.innerWidth < 768) return undefined;
    if (!coupon) return undefined;
    if (!cartItems || cartItems.length === 0) return undefined;
    if (sessionStorage.getItem(SESSION_KEY) === '1') return undefined;
    if (localStorage.getItem('ss_applied_coupon')) return undefined;

    let armed = true;
    const onMouseOut = (e) => {
      if (!armed) return;
      // Only trigger when the cursor leaves through the TOP of the viewport
      if (e.clientY <= 0 && (e.relatedTarget == null || e.relatedTarget.nodeName === 'HTML')) {
        armed = false;
        sessionStorage.setItem(SESSION_KEY, '1');
        setOpen(true);
      }
    };
    document.addEventListener('mouseout', onMouseOut);
    return () => document.removeEventListener('mouseout', onMouseOut);
  }, [coupon, cartItems]);

  if (!open || !coupon) return null;

  const close = () => setOpen(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(coupon.code);
      setCopied(true);
      toast.success(`Code ${coupon.code} copied`);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error('Copy failed');
    }
  };

  const handleApplyAndCheckout = () => {
    localStorage.setItem('ss_applied_coupon', coupon.code);
    close();
    navigate('/checkout');
  };

  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
      data-testid="exit-intent-popup"
      role="dialog"
      aria-modal="true"
      onClick={close}
    >
      <div
        className="relative bg-[#FAF9F7] rounded-lg shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={close}
          data-testid="exit-popup-close"
          className="absolute top-3 right-3 text-[#8B7D6B] hover:text-[#2C2C2C] transition"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="bg-gradient-to-br from-[#6B4E37] to-[#8B6F4E] text-[#FAF9F7] py-6 px-6 text-center">
          <Sparkles className="h-7 w-7 mx-auto mb-2 text-[#E8D8C5]" />
          <h2 className="font-seasons text-2xl mb-1">Wait — don't go empty-handed</h2>
          <p className="font-montserrat text-sm text-[#E8D8C5]">
            Use code <span className="font-bold tracking-wider text-white">{coupon.code}</span> before you checkout
          </p>
        </div>

        <div className="p-6 text-center space-y-4">
          <p className="font-montserrat text-sm text-[#2C2C2C]">
            {coupon.banner_text}
          </p>
          {coupon.urgency_text && (
            <p className="font-montserrat text-[11px] tracking-[0.2em] uppercase text-[#8B7D6B]">
              {coupon.urgency_text}
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <Button
              onClick={handleCopy}
              variant="outline"
              data-testid="exit-popup-copy"
              className="flex-1 border-[#6B4E37] text-[#6B4E37] hover:bg-[#6B4E37] hover:text-white font-montserrat tracking-wider uppercase"
            >
              {copied ? <CheckCircle2 className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
              {copied ? 'Copied' : `Copy ${coupon.code}`}
            </Button>
            <Button
              onClick={handleApplyAndCheckout}
              data-testid="exit-popup-apply"
              className="flex-1 bg-[#C4B5A0] hover:bg-[#B0A090] text-white font-montserrat tracking-wider uppercase"
            >
              Apply & Checkout
            </Button>
          </div>

          <button
            type="button"
            onClick={close}
            className="text-[11px] font-montserrat text-[#8B7D6B] hover:text-[#2C2C2C] underline-offset-2 hover:underline"
          >
            No thanks, I'll skip the discount
          </button>
        </div>
      </div>
    </div>
  );
};
